import { environment } from "../../../environments/environment";
import { ApiEnvNames, SearchConfig } from "./api.model";
import { DataTableSchema, DefaultTableSchema } from "./dynamic-data-table.model";
import { SpinIdNumber, DealerCodeText, CertificationDropdown, defaultSearchContainerButtons, FieldNames, JobCodeDescriptionDropdown, StartDateDate, CompletionDateDate, EndDate } from './form-fields.model';
import { ButtonFieldConfig, DynamicFormArray, FormFieldConfig, Props, SECTION_TYPE, TabConfig } from "./form.model";
import { FETCH_METHODS } from "./common.model";
import { FilterListContainerComponent } from "../layout/filter-list-container/filter-list-container.component";
import { FormContainerComponent } from '../layout/form/form-container/form-container.component';
import { EliteBuySellDashboardComponent } from '../components/elite-buy-sell-dashboard/elite-buy-sell-dashboard.component';
import { DealerTransferSummaryComponent } from '../components/dealer-transfer-summary/dealer-transfer-summary.component';

export const getEliteCertificationBaseUrl = () => {
  return environment[ApiEnvNames['EliteCertification']];
};

export const EliteCertTitle = "Elite Certification";

export const EliteCertificationDataSchema = {
  [FieldNames['SpinId']]: 'spinId'
};

export const EliteCertificationReportTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),
  columnSchema: [
    { label: 'Dealer Code', fieldKey: 'dealerCode'},
    { label: 'SPIN', fieldKey: 'spinId'},
    { label: 'First Name', fieldKey: 'firstName' },
    { label: 'Middle Initial', fieldKey: 'middleInitial' },
    { label: 'Last Name', fieldKey: 'lastName' },
    { label: 'Job Code', fieldKey: 'jobCode' },
    { label: 'Job Code Desc', fieldKey: 'jobCodeDesc' },
    { label: 'Lexus Primary Job Code', fieldKey: 'lexusPrimaryJobCode' },
    { label: 'Cert Level', fieldKey: 'certLevel' }

  ],
  exportConfig: {
    fileName: 'Elite_Certification_Report',
    exportMessage: "Elite Certification Report",
    isCamelCase: ['firstName', 'middleInitial', 'lastName', 'jobCodeDesc', 'lexusPrimaryJobCode', 'certLevel'],
    schema: {
      dealerCode: 'Dealer Code',
      spinId: 'SPIN ID',
      firstName: 'First Name',
      middleInitial: 'Middle Initial',
      lastName: 'Last Name',
      jobCode: 'Job Code',
      jobCodeDesc: 'Job Code Desc',
      lexusPrimaryJobCode: 'Lexus Primary Job Code',
      certLevel: 'Cert Level'
    }
  },
  serverSidePaginatation: true,
  breadcrums: [{ level: 1, name: EliteCertTitle }]
  
}

export const DealerTransferAnalysisTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),
  tableTitle: 'Dealer Transfer Report',
  columnSchema: [
    { label: 'Old Dealer', fieldKey: 'oldDealer' },
    { label: 'New Dealer', fieldKey: 'newDealer' },
    { label: 'Old Associates Count', fieldKey: 'totalAssocInOldDealer' },
    { label: 'New Associates Count', fieldKey: 'totalAssocInNewDealer' },
    { label: 'Transferred Associates Count', fieldKey: 'totalAssocInBoth' },
    { label: 'Transfer %', fieldKey: 'transferPercentage', isPercent: true, isProgressBar: true },
  ],
  dataSource: [],
  isPaginationRequired: true,
  isSearchRequired: true,
  isSortRequired: true,
  breadcrums: [{ level: 1, name: EliteCertTitle }],
};

export const DealerTransferAnalysisSearchConfig: SearchConfig = {
  path: 'getBuySellDealerSummaryReport',
  method: FETCH_METHODS['Get'],
  urlPath: ApiEnvNames['EliteCertification'],
};

const FilterFields: FormFieldConfig[] = structuredClone([
  StartDateDate, EndDate, SpinIdNumber, DealerCodeText, JobCodeDescriptionDropdown, CertificationDropdown 
]);

const FormButtonArray: ButtonFieldConfig[] = structuredClone(defaultSearchContainerButtons).map(btn => ({
  ...btn,
  isDisabled: false,
  customEnableDisable: true,
}));

export const EliteCertificationReportSearchConfig: SearchConfig = {
  path: 'getEliteEnrollmentReport',
  method: FETCH_METHODS['Post'],
  urlPath: ApiEnvNames['EliteCertification'],
  body: {
    [FieldNames.SpinId]: '',
    [FieldNames.DealerCode]: '',
    [FieldNames.LexusPrimaryJobCode]: '',
    [FieldNames.Certification]: '',
  }
}

export const EliteCertificationExportSearchConfig: SearchConfig = {
  method: FETCH_METHODS['Post'],
  path: 'getSignedUrl',
  body: {
    bucketName: '',
    key: '',
    type: 'Download',
  }
}

export const EliteCertificationReportProps: Props = {
  isSearchVisible: false,
  isTabStretch: true
}

export const EliteCertificationReportFormArray: DynamicFormArray = {
  FieldsArray: FilterFields,
  ButtonsArray: FormButtonArray,
  title: 'Search Criteria',
  searchConfig: EliteCertificationReportSearchConfig,
  autoSearchOnLoad:true,
  alwaysEnableButtons: true
}

export const BuySellDealerReportFormArray: DynamicFormArray = {
  FieldsArray: [],
  ButtonsArray: structuredClone(defaultSearchContainerButtons),
  title: 'Search Criteria',
  autoSearchOnLoad: true
}

export const BuySellDealerReportSearchConfig: SearchConfig = {
  path: 'getBuySellDealerReport',
  method: FETCH_METHODS['Post'],
  urlPath: ApiEnvNames['EliteCertification'],
  body: {
    [FieldNames.OldDealer]: '',
    [FieldNames.NewDealer]: '',
    jobDescription: '',
    spinId: ''
  }
}

export const BuySellDealerReportTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),
  tableTitle: 'Buy/Sell Report',
  columnSchema: [
    { label: 'SPIN', fieldKey: 'spinId' },
    { label: 'Job Code', fieldKey: 'jobCode' },
    { label: 'Job Desc', fieldKey: 'jobDescription' },
    { label: 'Job Start Date', fieldKey: 'startDate', isFieldDate: true },
    { label: 'Job End Date', fieldKey: 'endDate', isFieldDate: true },
    { label: 'Old Dealer', fieldKey: 'oldDealer' },
    { label: 'New Dealer', fieldKey: 'newDealer' },
    { label: 'Status', fieldKey: 'status' }
  ],
  exportConfig: {
    fileName: 'Buy_Sell_Dealer_Report',
    exportMessage: 'Buy/Sell Dealer Report',
    isCamelCase: ['jobDescription'],
    dateFields: ['startDate', 'endDate'],
    schema: {
      spinId: 'SPIN',
      jobCode: 'Job Code',
      jobDescription: 'JOB DESC',
      startDate: 'JOB START DATE',
      endDate: 'JOB END DATE',
      oldDealer: 'OLD DEALER',
      newDealer: 'NEW DEALER',
      status: 'STATUS'
    }
  },
  breadcrums: [{ level: 1, name: EliteCertTitle }]
}

export const EliteDynamicOptionFields: any = {
  fields: [
    { name: FieldNames.LexusPrimaryJobCode, backendColumnMapping: { fieldLabel: 'lexusPrimaryJobCode', fieldValue: 'lexusPrimaryJobCode' } }
  ],
  searchConfig: {
    path: 'getElitePrimaryJobList',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EliteCertification']
  }
}

export const EliteBuySellGridSchema = [
  {
    component: FormContainerComponent,
    type: SECTION_TYPE.GlobalFilter,
    inputs: {
      dynamicFormArray: {
        formName: 'buySellGlobalFilter',
        FieldsArray: structuredClone([
          {
            ...structuredClone(StartDateDate),
            fieldName: FieldNames.StartDate,
            displayName: 'Start Date',
            required: true,
            defaultValue: '01/03/2026',
            selectedValue: new Date(2026, 0, 3),
            max: new Date(2027, 11, 31),
          },
          {
            ...structuredClone(CompletionDateDate),
            fieldName: FieldNames.endDate,
            displayName: 'End Date',
            required: true,
            defaultValue: '01/04/2027',
            selectedValue: new Date(2027, 0, 4),
            max: new Date(2027, 11, 31),
          },
        ]),
        searchConfig: {
          method: 'POST',
          path: '',
          body: {
            startDate: '',
            endDate: ''
          }
        }
      }
    }
  },
  {
    component: DealerTransferSummaryComponent,
    type: SECTION_TYPE.Chart,
    inputs: {},
  },
  {
    component: FilterListContainerComponent,
    type: SECTION_TYPE.List,
    inputs: {
      elementId: 'BuySellDealerReport',
      DataTableSchema: structuredClone(BuySellDealerReportTableSchema),
      filterSchema: { FieldsArray: [], ButtonsArray: [], title: '', autoSearchOnLoad: false },
      onLoadFetch: true,
      forSection: true,
      apiConfig: structuredClone(BuySellDealerReportSearchConfig),
      globalFilterFields: [FieldNames['StartDate'], FieldNames['endDate'], 'buySell'],
      groupType: 'buySell',
    },
  },
];


export const getEliteCertificationTabConfig = (eliteTabComponent?: any): Array<TabConfig> => {
  return [
    {
      label: 'Elite Certification Report',
      tabId: 'elite-certification',
      type: SECTION_TYPE.List,
      component: eliteTabComponent,
      inputs: eliteTabComponent ? {} : ({
        elementId: 'EliteCertificationReport',
        DataTableSchema: structuredClone(EliteCertificationReportTableSchema),
        forSection: false,
        filterSchema: structuredClone(EliteCertificationReportFormArray),
        onLoadFetch: false,
        apiConfig: structuredClone(EliteCertificationReportSearchConfig),
        globalFilterFields: ['elite'],
        groupType: 'elite',
      } as any),
    },
    {
      label: 'Buy Sell',
      tabId: 'buy-sell',
      type: SECTION_TYPE.List,
      component: EliteBuySellDashboardComponent,
      inputs: { gridSchema: EliteBuySellGridSchema },
    }
  ];
};

